import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { AppButton, Spinning } from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ProductOfferingType } from '@RedflagAI/node-utility/interface';

const Section = ({ title, children, theme }) => (
  <Box
    pad="1rem"
    gap="0.5rem"
    round="var(--app-radius)"
    border={{ color: theme.page.border, size: '1px' }}
    background={theme.page.altBg}
  >
    <Text
      size="0.82rem"
      weight={700}
      color={theme.page.secondaryText}
      style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
    >
      {title}
    </Text>
    {children}
  </Box>
);

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  theme: PropTypes.object.isRequired,
};

const Row = ({
  label, value = '', theme,
}) => (
  <Box direction="row" gap="0.5rem">
    <Text size="0.82rem" color={theme.page.secondaryText} style={{ minWidth: '140px' }}>
      {`${label}:`}
    </Text>
    <Text size="0.82rem" color={theme.page.primaryText}>{value || '—'}</Text>
  </Box>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  theme: PropTypes.object.isRequired,
};

const Step6Review = ({
  contact,
  accountType,
  selectedProducts,
  productConfigs,
  extraItems,
  terms,
  onSubmit,
  submitLoading,
  submitError = null,
}) => {
  const { theme } = useAppTheme();
  const { page, button } = theme;

  const renderProductConfig = (product, config) => {
    const planName = config.planMode === 'existing'
      ? (config.planLabel || config.planId || 'Selected plan')
      : (config.customPlan?.name || 'Custom plan');

    const cfg = config.config || {};
    const data = config.customPlan?.data || {};
    const isCustom = config.planMode !== 'existing';

    const renderPricingRows = () => {
      if (!isCustom) return null;
      if (product.productType === ProductOfferingType.CID) {
        return (
          <>
            {(data.setupFeeAmount !== '' && data.setupFeeAmount !== undefined) && (
              <Row label="Setup fee" value={`$${data.setupFeeAmount}`} theme={theme} />
            )}
            {(data.monthlyFeeAmount !== '' && data.monthlyFeeAmount !== undefined) && (
              <Row label="Monthly fee" value={`$${data.monthlyFeeAmount}`} theme={theme} />
            )}
            {(data.revenueSharePercent !== '' && data.revenueSharePercent !== undefined) && (
              <Row
                label="Revenue share"
                value={`${data.revenueSharePercent}%${data.revenueShareStartMonth > 0 ? ` (from month ${data.revenueShareStartMonth})` : ''}`}
                theme={theme}
              />
            )}
          </>
        );
      }
      if (product.productType === ProductOfferingType.SOCIAL) {
        return (
          <>
            {(data.defaultPerPlatformAmount !== '' && data.defaultPerPlatformAmount !== undefined) && (
              <Row label="Per platform" value={`$${data.defaultPerPlatformAmount}`} theme={theme} />
            )}
          </>
        );
      }
      return (
        <>
          {(data.setupFee !== '' && data.setupFee !== undefined) && (
            <Row label="Setup fee" value={`$${data.setupFee}`} theme={theme} />
          )}
          {(data.amount !== '' && data.amount !== undefined) && (
            <Row label="Amount" value={`$${data.amount}`} theme={theme} />
          )}
          {data.frequency && (
            <Row label="Frequency" value={data.frequency} theme={theme} />
          )}
        </>
      );
    };

    return (
      <Box gap="0.3rem">
        <Row label="Plan" value={planName} theme={theme} />
        {product.productType === ProductOfferingType.CID && (
          <>
            <Row label="Uploader" value={cfg.uploader} theme={theme} />
            <Row
              label="Channels"
              value={(cfg.channels || []).filter((c) => c.trim()).join(', ')}
              theme={theme}
            />
          </>
        )}
        {product.productType === ProductOfferingType.SOCIAL && (
          <>
            <Row label="Platforms" value={(cfg.platforms || []).join(', ')} theme={theme} />
            {cfg.mainLink && <Row label="Main link" value={cfg.mainLink} theme={theme} />}
          </>
        )}
        {renderPricingRows()}
      </Box>
    );
  };

  return (
    <Box gap="1rem">
      <Text size="0.95rem" weight={700} color={page.primaryText}>
        Review your configuration before submitting.
      </Text>
      <Section title="Account Details" theme={theme}>
        <Row label="Email" value={contact.email} theme={theme} />
        <Row label="Full Name" value={contact.fullName} theme={theme} />
        <Row label="Company" value={contact.companyName} theme={theme} />
        <Row label="Phone" value={contact.phone} theme={theme} />
        <Row label="Country" value={contact.country} theme={theme} />
        <Row label="Account Type" value={accountType} theme={theme} />
      </Section>
      {selectedProducts.map((product, idx) => (
        <Section key={product.productLabel} title={product.productLabel} theme={theme}>
          {renderProductConfig(product, productConfigs[idx] || {})}
        </Section>
      ))}
      {extraItems.length > 0 && (
        <Section title="Extra Items" theme={theme}>
          {extraItems.map((item) => (
            <Box key={item.id} gap="0.2rem">
              <Text size="0.82rem" color={page.primaryText} weight={600}>
                {item.description}
              </Text>
              <Text size="0.78rem" color={page.secondaryText}>
                {`${item.kind} — `}
                {item.valueType === 'amount' ? `$${item.value}` : `${item.value}%`}
                {` — ${item.frequency}${item.productKey ? ` (${item.productKey})` : ' (all products)'}`}
              </Text>
            </Box>
          ))}
        </Section>
      )}
      <Section title="Terms & Conditions" theme={theme}>
        <Row label="Source" value={terms.source} theme={theme} />
        {terms.source === 'custom' && (
          <Row label="File" value={terms.uploadedFileName || 'uploaded'} theme={theme} />
        )}
      </Section>
      <Box gap="0.5rem">
        {submitError && (
          <Box
            pad="0.75rem"
            round="var(--app-radius)"
            background="rgba(255,0,0,0.08)"
            border={{ color: 'var(--app-error)', size: '1px' }}
          >
            <Text size="0.85rem" color="var(--app-error)">{submitError}</Text>
          </Box>
        )}
        <AppButton
          overrideHover
          level="tableControls"
          width="fit-content"
          bgColor={button.primaryBg}
          color={button.primaryText}
          fontWeight={600}
          disabled={submitLoading}
          onClick={onSubmit}
          label={(
            <Box direction="row" gap="0.5rem" align="center">
              {submitLoading && <Spinning size="small" color="white" />}
              <Text color="white" size="0.9rem">Confirm & Generate Link</Text>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

Step6Review.propTypes = {
  contact: PropTypes.object.isRequired,
  accountType: PropTypes.string.isRequired,
  selectedProducts: PropTypes.array.isRequired,
  productConfigs: PropTypes.array.isRequired,
  extraItems: PropTypes.array.isRequired,
  terms: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  submitError: PropTypes.string,
};

export default Step6Review;
